/**
 * Simple module-level inbox cache for instant mailbox loading.
 *
 * Usage:
 * 1. On login (PrivateRoutes.tsx): call prefetchInbox() to populate cache + trigger DB sync
 * 2. On UnifiedMailbox mount: call getCache() to get instant data
 * 3. On background refresh: call setCache() to update
 */

import { USER_ACCESS_TOKEN } from '@constants/auth';
import { tokenStorage } from '@utils/tokenStorage';

const EMAIL_API_URL = import.meta.env.VITE_EMAIL_API_URL || 'https://email.scrapeking.com';

export interface EmailBoxResponse {
  accountThreads: {
    accountId: string;
    accountEmail: string;
    threads: {
      initialMessageId: string;
      subject: string;
      latestEmailInThread: {
        type: string;
        messageId: string;
        time: string;
        body: string;
        from: string[];
        to: string[];
        references?: string[];
        inReplyTo?: string;
      };
      totalEmailsInThread: number;
      isWarmup?: boolean;
      hasLeadReply?: boolean;
      replySentiment?: 'interested' | 'neutral' | 'negative' | 'auto_reply';
      category?: 'lead' | 'auto_reply' | 'security' | 'bounce' | 'notification';
    }[];
  }[];
}

interface CacheEntry {
  data: EmailBoxResponse;
  timestamp: number;
}

let cache: CacheEntry | null = null;

const DEFAULT_MAX_AGE_MS = 5 * 60 * 1000; // 5 minutes

export function getCache(): EmailBoxResponse | null {
  if (!cache) return null;
  return cache.data;
}

export function setCache(data: EmailBoxResponse): void {
  cache = {
    data,
    timestamp: Date.now(),
  };
}

export function isStale(maxAgeMs: number = DEFAULT_MAX_AGE_MS): boolean {
  if (!cache) return true;
  return Date.now() - cache.timestamp > maxAgeMs;
}

export function clearCache(): void {
  cache = null;
}

export function getCacheAge(): number | null {
  if (!cache) return null;
  return Date.now() - cache.timestamp;
}

/**
 * Pre-fetch inbox data and trigger backend IMAP-to-DB sync.
 * Call this on login to ensure instant mailbox loading.
 * Runs completely in the background — never blocks or throws.
 */
export async function prefetchInbox(): Promise<void> {
  try {
    const token = tokenStorage.getToken(USER_ACCESS_TOKEN);
    if (!token) return;

    const headers = {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    };

    // 1. Trigger backend IMAP-to-DB sync (fire-and-forget, don't await)
    fetch(`${EMAIL_API_URL}/master-inbox/sync`, {
      method: 'POST',
      headers,
    }).catch(() => {}); // Ignore errors — best-effort

    // 2. Fetch inbox data to populate the in-memory cache
    const response = await fetch(`${EMAIL_API_URL}/master-inbox?boxName=INBOX&includeWarmup=true&limit=50`, {
      headers,
    });

    if (response.ok) {
      const data: EmailBoxResponse = await response.json();
      setCache(data);
      console.log('[inboxCache] Pre-fetched inbox on login:', data.accountThreads.length, 'accounts');
    }
  } catch {
    // Silently fail — this is a performance optimization, not critical
  }
}
